import React, { useEffect, useMemo, useState } from 'react';

import Input from '@/components/General/inputs/Input';
import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import InputSection from '@/components/General/inputs/InputSection';
import { Query } from '@/utils/react-query/Queries';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import { reportBibleStudySession } from '@/data/bibleStudies';
import Toast from 'react-native-toast-message';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import CustomField from '@/components/CustomFields/CustomField';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { supabase } from '@/data/client';
import Avatar from '@/components/People/Avatar';
import { View, Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import useBibleStudies from '@/utils/hooks/useBibleStudies';
import BibleStudySetupNeeded from '@/components/BibleStudies/BibleStudySetupNeeded';
const EMPTY_DRAFT = {};

export default function ReportBibleStudySessionModal() {
    const { smallGroupID } = useLocalSearchParams()
    const storeKey = "new"

    const queryClient = useQueryClient()
    const router = useRouter()

    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const sessionDetails = useEditScreenStore(s => s.AddBibleStudySessionModal?.[storeKey]) || EMPTY_DRAFT

    const { data: loggedInUser } = Query.people.loggedInUser()
    const { enabled, loading, canView, canManage, canCreate, setupNeeded, bibleStudyType, roleID } = useBibleStudies();

    // Flag/permission guard — UI gating only; the RPC validates access.
    useEffect(() => {
        if (!loading && (!enabled || !canView)) {
            router.canGoBack() ? router.back() : router.replace("/");
        }
    }, [loading, enabled, canView, router]);

    const { data: selectedStudy } = Query.smallGroups.byId({id: smallGroupID, options: { enabled: !!smallGroupID }})
    const { data: bibleStudyTypeSections } = Query.customFieldSections.all({module: "SmallGroupType", options: { enabled: !!bibleStudyType?.id }});
    const autoSelectMembers = bibleStudyType?.autoSelectMembersOnSessionReport !== false;

    // Attendee picker defaults to the study's core students; toggling off
    // opens the full People directory for guests.
    const [onlyStudyStudents, setOnlyStudyStudents] = useState(true);
    const activeSmallGroupID = sessionDetails?.smallGroupID || smallGroupID;

    // Study picker — purpose-scoped dropdown (only Bible Study type rows, and
    // closed studies are excluded by the dropdown source).
    const studiesQuery = Query.bibleStudies.forDropdown({ smallGroupTypeID: bibleStudyType?.id });
    const studiesQueryResult = useMemo(() => ({
        data: studiesQuery.data || [],
        isFetching: studiesQuery.isFetching,
        refetch: studiesQuery.refetch,
    }), [studiesQuery.data, studiesQuery.isFetching, studiesQuery.refetch]);

    const studyStudentsQuery = useInfiniteQuery({
        queryKey: ['smallGroupMembers', activeSmallGroupID],
        queryFn: async () => {
            const { data, error } = await supabase.from('SmallGroupMembers')
                .select('Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
                .eq('smallGroupID', activeSmallGroupID);
            if (error) throw error;
            const people = data?.map(r => r.Person).filter(Boolean) || [];
            people.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
            return { nextPage: undefined, data: people };
        },
        getNextPageParam: () => undefined,
        enabled: !!activeSmallGroupID && onlyStudyStudents,
    });

    // SheetSelector for type="people" expects an infinite-query shape: { pages: [{ data: [] }] }
    const attendeesQueryResult = useMemo(() => ({
        data: { pages: [{ data: studyStudentsQuery.data?.pages?.[0]?.data || [] }] },
        isFetching: studyStudentsQuery.isFetching,
        refetch: studyStudentsQuery.refetch,
    }), [studyStudentsQuery.data, studyStudentsQuery.isFetching, studyStudentsQuery.refetch]);

    // Teacher picker limited to rostered teachers (type roleID → RolePeople).
    const rolePeopleQuery = useInfiniteQuery({
        queryKey: ['peopleByRole', roleID],
        queryFn: async ({pageParam: _pageParam = 0}) => {
            const { data, error } = await supabase.from('RolePeople')
                .select('Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
                .eq('roleID', roleID);
            if (error) throw error;
            const people = data?.map(r => r.Person).filter(Boolean) || [];
            people.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || ''));
            return { nextPage: undefined, data: people };
        },
        getNextPageParam: () => undefined,
        enabled: !!roleID
    });

    const reportSessionMutation = useMutation({
        mutationFn: reportBibleStudySession,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['bibleStudies']});
            queryClient.invalidateQueries({queryKey: ['smallGroups']});
            queryClient.invalidateQueries({queryKey: ['dashboardData']});
            queryClient.invalidateQueries({queryKey: ['people']});
            removeEditScreenData("AddBibleStudySessionModal", storeKey)
            redirectToDefault(router, "AddBibleStudySessionModal")
            Toast.delayShow({type: 'success', text2: 'Session has been reported successfully!'})
        }
    })

    /*** Effects ***/
    useEffect(() => {
        if(selectedStudy){
            setSessionDetails({
                ...sessionDetails,
                smallGroupID: smallGroupID,
                SmallGroup: selectedStudy,
                Teacher: selectedStudy?.Leader,
                teacherID: selectedStudy?.Leader?.id,
                // Honor autoSelectMembersOnSessionReport — default attendees to
                // the current core roster; reporter adjusts for reality/guests.
                Attendees: autoSelectMembers ? selectedStudy?.Students?.map(s => s.Person) : (sessionDetails?.Attendees || []),
            })
        }
    }, [selectedStudy, autoSelectMembers])

    const setSessionDetails = (details) => {
        setEditScreenData("AddBibleStudySessionModal", storeKey, details)
    }

    const setPeopleNote = (personID, content) => {
        setSessionDetails({
            ...sessionDetails,
            peopleNotes: { ...(sessionDetails?.peopleNotes || {}), [personID]: content }
        });
    };

    if (enabled && setupNeeded && !loading) {
        return <BibleStudySetupNeeded canManage={canManage} />;
    }

    const attendees = sessionDetails?.Attendees || [];

    return (
        <ModalWrapper
            loading={reportSessionMutation.isPending}
            onConfirm={() => {
                if (!sessionDetails.smallGroupID) {
                    Toast.show({ type: 'error', text2: 'Please select a Bible Study' });
                    return;
                }

                if (!sessionDetails.sessionDate) {
                    Toast.show({ type: 'error', text2: 'Please select a session date' });
                    return;
                }

                if (!sessionDetails.teacherID) {
                    Toast.show({ type: 'error', text2: 'Please select the Teacher for this session' });
                    return;
                }

                const missingRequiredFields = (bibleStudyType?.CustomFields || []).filter(f => f.displayOn === 'session' && f.required && !f.isDeleted);
                const missingField = missingRequiredFields.find(f => {
                    const val = sessionDetails.customFieldValues?.[f.id];
                    return val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
                });
                if(missingField){
                    Toast.show({type: 'error', text2: `Please fill out the required field: ${missingField.name}`})
                    return;
                }

                // One save → one atomic report_bible_study_session call.
                // p_people_notes rows link student notes to person + study +
                // the new session server-side (visibility='BIBLE_STUDY').
                const peopleNotes = Object.entries(sessionDetails.peopleNotes || {})
                    .filter(([pid, content]) => content?.trim() && attendees.some(a => a.id === pid))
                    .map(([personID, content]) => ({ personID, content: content.trim() }));

                reportSessionMutation.mutate({
                    smallGroupID: sessionDetails.smallGroupID,
                    startTime: sessionDetails.sessionDate,
                    endTime: sessionDetails.endTime || null,
                    teacherID: sessionDetails.teacherID,
                    attendeeIDs: attendees.map(a => a.id),
                    sessionNote: sessionDetails.sessionNote?.trim() || null,
                    peopleNotes,
                    customFieldValues: sessionDetails.customFieldValues || null,
                })
            }}
            title="Report a Session"
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
                <SheetSelector
                    required
                    type="smallGroups"
                    queryResult={studiesQueryResult}
                    title="Bible Study"
                    addScreenName={canCreate ? "biblestudies/create" : null}
                    fromScreen={`biblestudies/sessions/create`}
                    fromStoreKey={storeKey}
                    value={sessionDetails?.SmallGroup}
                    onChange={(selected_item) => {
                        setSessionDetails({
                            ...sessionDetails,
                            smallGroupID: selected_item?.id,
                            SmallGroup: selected_item,
                        })
                    }}
                />

                <InputSection title="Teacher" description="Who actually taught this session? Defaults to the primary teacher — record substitutes accurately.">
                    <SheetSelector
                        required
                        type="people"
                        queryResult={roleID ? rolePeopleQuery : undefined}
                        title="Teacher"
                        fromScreen={`biblestudies/sessions/create`}
                        fromStoreKey={storeKey}
                        value={sessionDetails?.Teacher}
                        onChange={(selected_item) => {
                            setSessionDetails({
                                ...sessionDetails,
                                teacherID: selected_item?.id,
                                Teacher: selected_item
                            })
                        }}
                    />
                </InputSection>

                <DatePickerInput
                    required
                    title="Date of Session"
                    mode='date'
                    value={sessionDetails.sessionDate}
                    onUpdate={(new_value) => {
                        setSessionDetails({
                            ...sessionDetails,
                            sessionDate: new_value
                        })
                    }}
                />

                <DatePickerInput
                    title="End Time (optional)"
                    mode='datetime'
                    value={sessionDetails.endTime}
                    onUpdate={(new_value) => {
                        setSessionDetails({
                            ...sessionDetails,
                            endTime: new_value
                        })
                    }}
                />

                <InputSection title="Attendees" description="Core students are preselected — uncheck anyone absent, and add guests from the directory.">
                    {!!activeSmallGroupID && (
                        <ToggleRow
                            label="Only show students in this study"
                            description="Filter the attendee list to this study's roster. Turn off to add guests from all people."
                            value={onlyStudyStudents}
                            onValueChange={setOnlyStudyStudents}
                        />
                    )}

                    <SheetSelector multiple noDefaultTruncated type="people" title="Students & Guests" addScreenName={loggedInUser?.can?.("Create People") ? "people/create" : null} fromScreen={`biblestudies/sessions/create`} fromStoreKey={storeKey} queryResult={onlyStudyStudents && activeSmallGroupID ? attendeesQueryResult : undefined} value={attendees} onChange={(selected_items) => {
                        setSessionDetails({
                            ...sessionDetails,
                            Attendees: selected_items
                        })
                    }}/>
                </InputSection>

                <Input
                    title="Session Notes"
                    helpText="Overall notes for this session. Only the study's teachers and Bible Study managers can read them."
                    multiline
                    value={sessionDetails.sessionNote}
                    onUpdate={(new_value) => {
                        setSessionDetails({
                            ...sessionDetails,
                            sessionNote: new_value
                        })
                    }}
                />

                {attendees.length > 0 && (
                    <InputSection title="Student Notes" description="Optional per-student notes — saved to each student's profile, visible only to this study's teachers and managers.">
                        {attendees.map(person => (
                            <View key={person.id} style={styles.studentNoteRow}>
                                <View style={styles.studentNoteHeader}>
                                    <Avatar profile={person} size={24} disableBorder />
                                    <Text style={styles.studentNoteName}>{person.firstName} {person.lastName}</Text>
                                </View>
                                <Input
                                    multiline
                                    placeholder={`Note about ${person.firstName}…`}
                                    value={sessionDetails?.peopleNotes?.[person.id] || ''}
                                    onUpdate={(v) => setPeopleNote(person.id, v)}
                                />
                            </View>
                        ))}
                    </InputSection>
                )}

                {(bibleStudyTypeSections || []).map(section => {
                    const sectionFields = (bibleStudyType?.CustomFields || []).filter(f => f.sectionID === section.id && f.displayOn === 'session');
                    if (sectionFields.length === 0) return null;
                    return (
                        <InputSection key={section.id} title={section.name}>
                            {sectionFields.map(field => (
                                <CustomField
                                    key={field.id}
                                    fieldType={field.type}
                                    required={field.required}
                                    onUpdate={(val) => {
                                        const oldVals = sessionDetails.customFieldValues || {};
                                        setSessionDetails({ customFieldValues: { ...oldVals, [field.id]: val } });
                                    }}
                                    options={{
                                        title: field.name,
                                        value: sessionDetails.customFieldValues?.[field.id],
                                        options: field.options,
                                    }}
                                />
                            ))}
                        </InputSection>
                    );
                })}
            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create((theme) => ({
    studentNoteRow: {
        marginBottom: 12,
        gap: 6,
    },
    studentNoteHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    studentNoteName: {
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: theme.colors.text,
    },
}));
